import baseConfiguratorReducer from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Reducer';
import { get } from 'lodash';
import { clampEditorItemPosition, computeDesignSize } from '../../utils/designSize';
import { buildVectorizeColorsMap } from '../../utils/buildDesignData';
import { DEFAULT_CANVAS_HEIGHT_MM, DEFAULT_CANVAS_WIDTH_MM } from '../../utils/editorUnits';
import { getDefaultCanvasSize } from './Selectors';
import {
  actionTypes,
  applyEditorUpdate,
  RECEIVE_OPTIONS,
} from './Actions';
import { createInitialEditorState } from './types';
import type { EditorItem, ImageItem } from './types';

function recomputeEditor(editor) {
  const designSize = computeDesignSize(editor.objects);
  return { ...editor, designSize };
}

function withCanvasBounds(state, item: EditorItem): EditorItem {
  const canvasSize = getDefaultCanvasSize(state);
  const canvasW = canvasSize.width || DEFAULT_CANVAS_WIDTH_MM;
  const canvasH = canvasSize.height || DEFAULT_CANVAS_HEIGHT_MM;
  return clampEditorItemPosition(item, canvasW, canvasH);
}

export default function configuratorReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_EDITOR_SELECTED_ID:
      return applyEditorUpdate(state, (editor) => ({
        ...editor,
        selectedId: action.selectedId,
      }));

    case actionTypes.SET_EDITOR_ADD_MODE:
      return applyEditorUpdate(state, (editor) => ({
        ...editor,
        addMode: action.addMode,
      }));

    case actionTypes.ADD_EDITOR_ITEM:
      return applyEditorUpdate(state, (editor) => {
        const item = withCanvasBounds(state, action.item);
        return recomputeEditor({
          ...editor,
          objects: { ...editor.objects, [item.id]: item },
          selectedId: item.id,
          addMode: null,
        });
      });

    case actionTypes.UPDATE_EDITOR_ITEM:
      return applyEditorUpdate(state, (editor) => {
        const existing = editor.objects[action.id];
        if (!existing) {
          return editor;
        }
        const item = withCanvasBounds(state, { ...existing, ...action.patch } as EditorItem);
        return recomputeEditor({
          ...editor,
          objects: {
            ...editor.objects,
            [action.id]: item,
          },
        });
      });

    case actionTypes.REMOVE_EDITOR_ITEM:
      return applyEditorUpdate(state, (editor) => {
        const { [action.id]: removed, ...rest } = editor.objects;
        return recomputeEditor({
          ...editor,
          objects: rest,
          selectedId: editor.selectedId === action.id ? null : editor.selectedId,
        });
      });

    case actionTypes.SET_EDITOR_ITEM_COLOR:
      return applyEditorUpdate(state, (editor) => {
        const existing = editor.objects[action.id];
        if (!existing || existing.type !== 'text') {
          return editor;
        }
        return {
          ...editor,
          objects: {
            ...editor.objects,
            [action.id]: { ...existing, color: action.color, colorOptionId: action.colorOptionId },
          },
        };
      });

    case actionTypes.SET_EDITOR_IMAGE_COLOR_AREA:
      return applyEditorUpdate(state, (editor) => {
        const existing = editor.objects[action.id] as ImageItem | undefined;
        if (!existing || existing.type !== 'image' || !existing.colorAreas?.length) {
          return editor;
        }
        if (action.areaIndex < 0 || action.areaIndex >= existing.colorAreas.length) {
          return editor;
        }

        const colorAreas = existing.colorAreas.map((area, index) =>
          index === action.areaIndex
            ? { color: action.color, colorOptionId: action.colorOptionId }
            : area
        );
        const imageData = existing.imageData || {};
        const operations = (imageData.operations as Record<string, unknown>) || {};
        const vectorizeColorsMap = buildVectorizeColorsMap(colorAreas);

        return {
          ...editor,
          objects: {
            ...editor.objects,
            [action.id]: {
              ...existing,
              colorAreas,
              imageData: {
                ...imageData,
                operations: {
                  ...operations,
                  vectorizeColorsMap,
                },
              },
            },
          },
        };
      });

    case actionTypes.RESET_EDITOR_IMAGE_COLOR_AREAS:
      return applyEditorUpdate(state, (editor) => {
        const existing = editor.objects[action.id] as ImageItem | undefined;
        if (!existing || existing.type !== 'image' || !existing.initialColorAreas?.length) {
          return editor;
        }
        const colorAreas = existing.initialColorAreas;
        const imageData = existing.imageData || {};
        const operations = (imageData.operations as Record<string, unknown>) || {};
        const vectorizeColorsMap = buildVectorizeColorsMap(colorAreas);
        return {
          ...editor,
          objects: {
            ...editor.objects,
            [action.id]: {
              ...existing,
              colorAreas,
              imageData: {
                ...imageData,
                operations: {
                  ...operations,
                  vectorizeColorsMap,
                },
              },
            },
          },
        };
      });

    case actionTypes.SET_EDITOR_ITEM_OPTION:
      return applyEditorUpdate(state, (editor) => {
        const existing = editor.objects[action.id];
        if (!existing) {
          return editor;
        }
        return {
          ...editor,
          objects: {
            ...editor.objects,
            [action.id]: {
              ...existing,
              selectedOptions: {
                ...(existing.selectedOptions ?? {}),
                [action.classificationId]: action.optionIdentifier,
              },
            },
          },
        };
      });

    case actionTypes.SET_BLOCKED_MATERIAL_ID:
      return { ...state, blockedMaterialId: action.materialId };

    case RECEIVE_OPTIONS: {
      const updatedState = baseConfiguratorReducer(state, action);
      const selectedoptionclassificationIdentifier = get(
        updatedState,
        'selectedoptionclassification.identifier'
      );
      const withOptions = {
        ...updatedState,
        optionsList: {
          ...(state.optionsList || {}),
          [selectedoptionclassificationIdentifier]: updatedState.options,
        },
      };
      const editor = get(withOptions, 'configuration.customdata.editor');
      if (editor) {
        return applyEditorUpdate(withOptions, () => editor);
      }
      return withOptions;
    }

    default: {
      const prevEditor = get(state, 'configuration.customdata.editor');
      const updatedState = baseConfiguratorReducer(state, action);
      const editor = get(updatedState, 'configuration.customdata.editor');
      if (get(updatedState, 'configuration') && !editor) {
        return applyEditorUpdate(updatedState, () => prevEditor || createInitialEditorState());
      }
      if (editor) {
        return applyEditorUpdate(updatedState, () => editor);
      }
      return updatedState;
    }
  }
}
